import { FlatList, Platform, Text, View } from "react-native";
import { useMutation, useQuery } from "@apollo/client";
import {
  CheckboxItem,
  ScrollableContainer,
  TextInput,
  useErrorScroll,
  ScreenContainer,
  Button,
  ActionsContainer,
  KeyboardAvoidingView,
  ErrorHelperText,
} from "../../components";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Employment, RootStackParamList, RootStackScreenProps, WorkingLocation } from "../types";
import FacilityPickerInput from "../../components/FacilityPickerInput";
import JobTitlePickerInput from "../../components/JobTitlePickerInput";
import TextInputArea from "../../components/TextInputArea";
import { ActivityIndicator, RadioButton } from "react-native-paper";
import { colors } from "../../../theme";
import SentForReviewModal from "../AccountScreen/SentForReviewModal";
import { JobTitleType } from "../../components/JobTitlePickerInput/JobTitlePickerInput";
import {
  ChangePrimaryEmploymentWarningModal,
  EmploymentCannotBeChangedWarningModal,
  WorkingLocationAffectSectionWarningModal,
} from "./WarningModals";
import {
  AddEmploymentsQuery,
  ChangeEmploymentMutationResponse,
  ChangeEmploymentMutationVariables,
  EmploymentsToRemove,
  FormData,
} from "./types";
import { CHANGE_EMPLOYMENT_MUTATION, ADD_EMPLOYMENT_QUERY } from "./query";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useIntercom } from "../../hooks";
import config from "../../config";
import EffectiveDateField from "./components/EffectiveDateField";
import UnsavedChangesModal from "../../components/UnsavedChangesModal";
import CompensationPopover from "../../components/CompensationPopover/CompensationPopover";

export const addEmploymentTitle = "Add Employment";

const defaultValues: FormData = {
  facility: null,
  jobTitle: "",
  jobClassification: "",
  otherJobTitle: "",
  jobDescription: "",
  effectiveDate: "",
  isPrimaryFacility: null,
  hasVerifiedEligibility: false,
  workingLocation: WorkingLocation.ON_SITE,
};

export enum EmploymentRequestStatus {
  Pending = "PENDING",
  Approved = "APPROVED",
  Invalid = "INVALID",
}

const AddEmploymentScreenContent = ({
  jobTitlesWithOther,
  initialIsPrimaryFacility,
  employmentsToRemove,
  currentEmployments,
  primaryEmployment,
  navigation,
  changeEmploymentFn,
  mutationLoading,
  returnTo,
  cameFromReviewEmployment,
}: {
  jobTitlesWithOther: JobTitleType[];
  initialIsPrimaryFacility: boolean | null;
  employmentsToRemove: EmploymentsToRemove[];
  currentEmployments?: Employment[];
  primaryEmployment?: Employment;
  navigation: NativeStackNavigationProp<RootStackParamList, "AddEmployment">;
  changeEmploymentFn: ({ variables }: { variables: ChangeEmploymentMutationVariables }) => Promise<any>;
  mutationLoading: boolean;
  returnTo?: () => void;
  cameFromReviewEmployment: boolean;
}) => {
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [sentForReviewModalVisible, setSentForReviewModalVisible] = useState<boolean>(false);
  const intercom = useIntercom();
  const [isChangePrimaryEmploymentWarningModalVisible, setIsChangePrimaryEmploymentWarningModalVisible] =
    useState<boolean>(false);
  const [isEmploymentCannotBeChangedModalVisible, setIsEmploymentCannotBeChangedModalVisible] = useState<boolean>(false);
  const [toConfirmWorkingLocation, setToConfirmWorkingLocation] = useState<WorkingLocation>();
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [discardChanges, setDiscardChanges] = useState<boolean>(false);
  const flatListRef = useRef<FlatList>(null);
  const { Divider, onSubmitError, scrollToField } = useErrorScroll({ defaultValues, flatListRef });
  const [showCompensationPopover, setShowCompensationPopover] = useState(false);

  const navigateOnSuccess = () => navigation.navigate("Account");

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm<FormData>({ defaultValues: { ...defaultValues, isPrimaryFacility: initialIsPrimaryFacility }, mode: "onChange" });
  const jobTitle = watch("jobTitle");

  function goBack() {
    if (window && window.history) {
      return window.history.back();
    }
    return navigation.goBack();
  }

  function goBackWeb() {
    if (cameFromReviewEmployment || discardChanges) {
      return goBack();
    }

    if (isDirty) {
      setShowUnsavedChangesModal(true);
      return;
    }
    goBack();
  }

  useEffect(() => {
    if (discardChanges) {
      return goBack();
    }
  }, [discardChanges]);

  useEffect(() => {
    if (cameFromReviewEmployment) return;
    if (Platform.OS === "web") return;
    const unsubscribe = navigation.addListener("beforeRemove", (event) => {
      if (isDirty) {
        event.preventDefault();
        setShowUnsavedChangesModal(true);
      }
    });

    return unsubscribe;
  }, [navigation, discardChanges, isDirty]);

  const handleEmploymentTypeChange = (newValue: string) => {
    const isNowPrimary = newValue === "true";

    if (isNowPrimary && primaryEmployment) {
      setIsChangePrimaryEmploymentWarningModalVisible(true);
    } else if (!isNowPrimary && currentEmployments?.length === 0) {
      setIsEmploymentCannotBeChangedModalVisible(true);
    } else {
      setValue("isPrimaryFacility", isNowPrimary, { shouldValidate: true });
    }
  };

  const handleWorkingLocationChange = (workingLocation: string) => {
    setToConfirmWorkingLocation(WorkingLocation[workingLocation as keyof typeof WorkingLocation]);
  };

  const onCancelWorkingLocation = () => {
    setToConfirmWorkingLocation(undefined);
  };

  const onConfirmWorkingLocation = () => {
    setToConfirmWorkingLocation(undefined);
    toConfirmWorkingLocation && setValue("workingLocation", toConfirmWorkingLocation);
  };

  const onSubmit = async (data: FormData) => {
    try {
      const response = await changeEmploymentFn({
        variables: {
          input: {
            facilityId: data.facility?.id,
            jobTitle: data.jobTitle,
            jobClassification: data.jobClassification,
            otherJobTitle: data.otherJobTitle,
            jobDescription: data.jobDescription,
            effectiveDate: data.effectiveDate,
            isPrimaryFacility: data.isPrimaryFacility,
            workingLocation: data.workingLocation,
            employmentsToRemove,
          },
        },
      });

      const { status, success, message } = response?.data?.changeEmployment || {};

      if (!success) {
        throw new Error(message);
      }

      if (status === EmploymentRequestStatus.Pending && success) {
        setSentForReviewModalVisible(true);
      }
      if (status === EmploymentRequestStatus.Approved && success) {
        setShowCompensationPopover(true);
        // showSnackBar({ message: "Employment Updated", icon: "check-circle", duration: 1500 });
        // returnTo ? returnTo() : navigateOnSuccess();
      }
    } catch (e) {
      setErrorMessage((e as Error).message || "Something went wrong");
    }
  };

  return (
    <ScreenContainer
      navigationTitle={addEmploymentTitle}
      testID="AddEmploymentScreen"
      goBack={() => navigation.goBack()}
      overrideBack={goBackWeb}
    >
      <SentForReviewModal
        visible={sentForReviewModalVisible}
        onClose={() => {
          setSentForReviewModalVisible(false);
          navigation.navigate("AccountEdit");
        }}
      />
      <ChangePrimaryEmploymentWarningModal
        isModalVisible={isChangePrimaryEmploymentWarningModalVisible}
        setIsModalVisible={setIsChangePrimaryEmploymentWarningModalVisible}
        primaryEmployment={primaryEmployment}
        setPrimaryEmployment={() => setValue("isPrimaryFacility", true)}
      />
      <EmploymentCannotBeChangedWarningModal
        isModalVisible={isEmploymentCannotBeChangedModalVisible}
        setIsModalVisible={setIsEmploymentCannotBeChangedModalVisible}
      />
      <WorkingLocationAffectSectionWarningModal
        isModalVisible={!!toConfirmWorkingLocation}
        onCancel={onCancelWorkingLocation}
        onConfirm={onConfirmWorkingLocation}
      />
      <UnsavedChangesModal
        mutationLoading={false}
        showUnsavedChangesModal={showUnsavedChangesModal}
        setShowUnsavedChangesModal={() => setShowUnsavedChangesModal(false)}
        discardAndExit={() => setDiscardChanges(true)}
        handleSubmit={() => setShowUnsavedChangesModal(false)}
        title="Exit and cancel employment change?"
        message="If you exit now, your changes will not be saved."
        buttonLabel="Continue"
        cancelButtonLabel="Exit & Cancel"
        size="md"
      />
      <KeyboardAvoidingView>
        <ScrollableContainer ref={flatListRef}>
          <View className="p-6 justify-between">
            <Text className="text-regular-tight font-700 text-ink-darkest">Employment Details</Text>

            <Text className="text-small-normal font-400 text-ink mt-4 mb-2">
              Please complete the following fields with details regarding your employment in the golf industry.
            </Text>
          </View>

          <Divider field="isPrimaryFacility" />

          <View className="p-6 justify-between mb-2">
            <Text className="text-regular-tight font-700 text-ink-darkest">Employment Type</Text>

            <Text className="text-small-normal font-400 text-ink mt-4 mb-2">
              You can have only one primary employment. Your primary facility determines your section affiliation and other
              membership details.{" "}
              <Text
                onPress={() => intercom.present(config.intercomContent.cantFindFacility)}
                className="underline text-primary text-small-normal font-400"
              >
                Learn More
              </Text>
            </Text>

            <Controller
              name="isPrimaryFacility"
              control={control}
              rules={{
                validate: (value) => value !== null || "Please select an employment type",
              }}
              render={({ field: { value } }) => (
                <RadioButton.Group
                  onValueChange={handleEmploymentTypeChange}
                  value={value === null ? "" : value ? "true" : "false"}
                >
                  <View className="flex flex-row mt-3">
                    <View className="flex flex-row items-center">
                      <RadioButton.Android
                        value="true"
                        status={value === true ? "checked" : "unchecked"}
                        uncheckedColor={colors.sky.DEFAULT}
                        testID="primary-facility-yes-btn"
                      />
                      <Text className="font-400 text-small-normal text-black mr-6">Primary</Text>
                    </View>

                    <View className="flex flex-row items-center">
                      <RadioButton.Android
                        value="false"
                        status={value === false ? "checked" : "unchecked"}
                        uncheckedColor={colors.sky.DEFAULT}
                        testID="primary-facility-no-btn"
                      />
                      <Text className="font-400 text-small-normal text-black">Secondary</Text>
                    </View>
                  </View>
                </RadioButton.Group>
              )}
            />
            {errors.isPrimaryFacility && (
              <ErrorHelperText error={errors.isPrimaryFacility}>{errors.isPrimaryFacility.message}</ErrorHelperText>
            )}
          </View>

          <Divider field="facility" />

          <View className="p-6 justify-between">
            <Text className="text-regular-tight font-700 text-ink-darkest mb-4">Facility</Text>
            <Controller
              name="facility"
              control={control}
              rules={{
                required: "Facility is required",
              }}
              render={({ field: { onChange, value } }) => (
                <FacilityPickerInput
                  title="Select Facility"
                  placeholder="Facility Name"
                  onChange={onChange}
                  value={value || undefined}
                  error={errors["facility"]}
                />
              )}
            />
          </View>

          <Divider field="jobTitle" />

          <View className="p-6 justify-between">
            <Text className="text-regular-tight font-700 text-ink-darkest">Job Title</Text>

            <Text className="text-small-normal font-400 text-ink mt-4 mb-2">
              Select your job title from the list. If your title is not listed, select "Other" and specify your title.
            </Text>
            <Controller
              name="jobTitle"
              control={control}
              rules={{
                required: "Job Title is required",
              }}
              render={({ field: { onChange, value } }) => (
                <JobTitlePickerInput
                  onChange={(item: JobTitleType) => {
                    setValue("jobClassification", item.jobClassification!);
                    onChange(item.jobTitle);
                  }}
                  value={value}
                  placeholder="Job Title"
                  jobTitlesData={jobTitlesWithOther}
                  error={errors["jobTitle"]}
                />
              )}
            />
            {jobTitle === "Other" ? (
              <View className="mt-2">
                <Controller
                  name="otherJobTitle"
                  control={control}
                  rules={{
                    required: jobTitle === "Other" ? "Other job title is required" : false,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      onChangeText={(text) => onChange(text)}
                      value={value}
                      placeholder="Add Your Job Title"
                      error={errors["otherJobTitle"]}
                    />
                  )}
                />
              </View>
            ) : null}
          </View>

          <Divider field="workingLocation" />

          <View className="p-6 justify-between">
            <Text className="text-regular-tight font-700 text-ink-darkest">Working Location</Text>

            <Controller
              name="workingLocation"
              control={control}
              rules={{
                required: "Working Location is required",
              }}
              render={({ field: { value } }) => (
                <RadioButton.Group onValueChange={handleWorkingLocationChange} value={value}>
                  <View className="flex flex-row mt-3">
                    <View className="flex flex-row items-center">
                      <RadioButton.Android
                        value={WorkingLocation.ON_SITE}
                        status={value === WorkingLocation.ON_SITE ? "checked" : "unchecked"}
                        uncheckedColor={colors.sky.DEFAULT}
                        testID="working-location-on-site-btn"
                      />
                      <Text className="font-400 text-small-normal text-black mr-6">On-site</Text>
                    </View>

                    <View className="flex flex-row items-center">
                      <RadioButton.Android
                        value={WorkingLocation.REMOTE}
                        status={value === WorkingLocation.REMOTE ? "checked" : "unchecked"}
                        uncheckedColor={colors.sky.DEFAULT}
                        testID="working-location-remote-btn"
                      />
                      <Text className="font-400 text-small-normal text-black">Remote/Regional</Text>
                    </View>
                  </View>
                </RadioButton.Group>
              )}
            />
          </View>

          <Divider field="jobDescription" />

          <View className="p-6 justify-between">
            <Text className="text-regular-tight font-700 text-ink-darkest">Job Description</Text>

            <Text className="text-small-normal font-400 text-ink mt-4 mb-2">
              Provide a list of your duties and responsibilities. Include any additional information about your facility that
              helps clarify your role and job function.
            </Text>

            <Controller
              name="jobDescription"
              control={control}
              rules={{
                required: "You must enter a job description",
              }}
              render={({ field: { onChange, value } }) => (
                <TextInputArea
                  onChangeText={(text) => onChange(text)}
                  value={value}
                  error={errors["jobDescription"]}
                  testID="jobDescriptionTextArea"
                  placeholder="Ex. I own and operate a golf shop at a PGA Recognized Golf Facility."
                  onFocus={() => scrollToField("jobDescription")}
                />
              )}
            />
          </View>

          <Divider field="effectiveDate" />

          <EffectiveDateField control={control} />

          <Divider field="hasVerifiedEligibility" />

          <View className="p-6 align-top">
            <Controller
              name="hasVerifiedEligibility"
              control={control}
              rules={{
                required: "You must verify your eligibility",
              }}
              render={({ field: { onChange, value } }) => (
                <CheckboxItem
                  value={value}
                  testID="verifiedEligibilityCheckbox"
                  onChange={onChange}
                  label="I verify that I meet the eligible employment requirements as defined by the PGA Constitution and Bylaws for the classification and employment I have submitted. The employment must show a pattern of employment that is regular, continuous, at the place of employment and provide the public with golf related goods and/or services. Members and Associates are cautioned to be factual, as falsification of information could result in disciplinary action."
                  labelStyle="text-regular-normal text-left mx-0"
                  className="items-start px-0"
                  error={errors["hasVerifiedEligibility"]}
                />
              )}
            />
          </View>
          <View>
            <ActionsContainer errorAlert errorMessage={errorMessage} onCloseErrorMessage={setErrorMessage}>
              <Button
                testID="submitButton"
                loading={mutationLoading}
                size="large"
                mode="contained"
                onPress={handleSubmit(onSubmit, onSubmitError)}
                isValid={isValid}
              >
                Submit
              </Button>
            </ActionsContainer>
          </View>
        </ScrollableContainer>
      </KeyboardAvoidingView>
      <CompensationPopover
        visible={showCompensationPopover}
        onDismiss={() => {
          setShowCompensationPopover(false);
          returnTo ? returnTo() : navigateOnSuccess();
        }}
      />
    </ScreenContainer>
  );
};

const AddEmploymentScreen = ({ route, navigation }: RootStackScreenProps<"AddEmployment">) => {
  const employmentsToRemove = route.params?.employmentsToRemove || [];
  const returnTo = route.params?.returnTo;
  const { data: employmentsData, loading: loadingJobTitles } = useQuery<AddEmploymentsQuery>(ADD_EMPLOYMENT_QUERY);

  const employments = employmentsData?.currentPerson?.employments;
  const [changeEmploymentFn, { loading }] = useMutation<ChangeEmploymentMutationResponse, ChangeEmploymentMutationVariables>(
    CHANGE_EMPLOYMENT_MUTATION
  );

  if (loadingJobTitles) {
    return <ActivityIndicator className="flex-1 items-center justify-center bg-white" />;
  }

  const currentEmployments = employments?.filter((employment) => !employmentsToRemove.some((e) => e.id === employment.id));

  const jobTitlesWithOther: JobTitleType[] = [
    ...employmentsData!.availableJobTitles.slice().sort((a, b) => a.jobTitle.localeCompare(b.jobTitle)),
    { id: "Other", jobTitle: "Other", jobClassification: "" },
  ];

  const primaryEmployment = currentEmployments?.find((employment: Employment) => employment.isPrimary);
  const initialIsPrimaryFacility = employmentsToRemove?.length === employments?.length ? true : null;

  return (
    <AddEmploymentScreenContent
      changeEmploymentFn={changeEmploymentFn}
      mutationLoading={loading}
      jobTitlesWithOther={jobTitlesWithOther}
      initialIsPrimaryFacility={initialIsPrimaryFacility}
      employmentsToRemove={employmentsToRemove}
      currentEmployments={currentEmployments}
      cameFromReviewEmployment={!!employmentsData?.currentPerson?.employments?.length}
      primaryEmployment={primaryEmployment}
      navigation={navigation}
      returnTo={returnTo}
    />
  );
};

export default AddEmploymentScreen;
